/**
 * SOLID Review
 * S — Good. One job: tell the user when the backend is unreachable. The consecutive-failure threshold and
 *     visibility-change reset are both in service of that single job (avoiding false-positive flashes),
 *     not separate concerns bolted on — the in-code comments explain why each exists.
 * O — Good. `POLL_INTERVAL_MS` and `FAILURE_THRESHOLD` are named constants that tune behavior without
 *     touching logic.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Zero props — mounted once at the App root, self-contained.
 * D — Strong. The health check arrives as a prop (the Application's use case, from the composition root);
 *     the report's type is the Domain schema's. No store, no adapter.
 */
import React, { useEffect, useState, useRef } from 'react';
import type { TClientUseCaseOutput } from '../../../application/ports/ClientUseCases.contract';

type IEntityHealthReport = TClientUseCaseOutput<'checkServerHealth'>;

/**
 * The health check arrives as a PROP, not from useComponentFramework(), and that is load-bearing.
 *
 * This banner renders OUTSIDE <ComponentFrameworkProvider> in App.tsx — deliberately, so that the
 * one component whose job is to say "the backend is unreachable" does not itself depend on the
 * provider being healthy. Calling the context hook here threw
 * "useComponentFramework must be used within <ComponentFrameworkProvider>", the ErrorBoundary
 * swallowed it, and the whole app rendered the error UI instead of a screen — the exact white-screen
 * failure the comment beside this component's mount records from 2026-08-17. Caught by
 * AppProviderCoverage, which exists for precisely that.
 */
export interface IPropsMaintenanceBanner {
  /**
   * The Application's `checkServerHealth` (strict-ports FE5a: it was the probe itself, `healthAdapter`),
   * handed over by the composition root. A call that fails counts as a failed probe.
   */
  readonly checkHealth: () => Promise<{ readonly success: true; readonly value: IEntityHealthReport } | { readonly success: false }>;
}

const POLL_INTERVAL_MS = 5_000;
// A single failed /health poll is almost always a transient blip (a dropped request,
// a brief mobile network stall) — not maintenance. Showing the banner on the first
// failure made it flash constantly. Require several CONSECUTIVE failures (~15s of real
// unreachability) before showing it; any success clears it immediately.
const FAILURE_THRESHOLD = 3;

/**
 * Full-width banner shown while the backend is unreachable (deploys, restarts,
 * outages). Polls `/health` on its own light interval and auto-hides on recovery,
 * so it needs no manual toggle around a deployment. Mounted at the App root so it
 * covers every screen (auth + game). The graphql-ws client reconnects on its own;
 * this banner only informs the user meanwhile.
 */
export const MaintenanceBanner: React.FC<IPropsMaintenanceBanner> = ({ checkHealth }) => {
  const [down, setDown] = useState(false);
  const failuresRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const onFailure = (): void => {
      failuresRef.current += 1;
      if (failuresRef.current >= FAILURE_THRESHOLD) setDown(true);
    };

    const check = async (): Promise<void> => {
      // Through the port (2026-08-30). This component used to own a fetch, a URL resolution and
      // its own failure handling — as did HealthIndicator, separately, with a second copy of all
      // three. The probe never throws, so the try/catch went with them: an unreachable server is
      // a REPORT (`reachable: false`), not an exception.
      const answer = await checkHealth();
      /* c8 ignore next -- unmount race during the async probe; not reproducible with act() */
      if (cancelled) return;
      if (answer.success && answer.value.ok) { failuresRef.current = 0; setDown(false); }
      else onFailure();
    };

    // Coming back from the background (e.g. turning the phone screen on) the network
    // stack may take a moment to be ready, so the first poll can spuriously fail. Reset
    // the failure streak and hide the banner on resume so a wake-up blip can't flash it,
    // then re-evaluate from a clean slate.
    const onVisible = (): void => {
      /* c8 ignore next -- non-browser guard; jsdom always defines document. */
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        failuresRef.current = 0;
        setDown(false);
        void check();
      }
    };

    void check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    /* c8 ignore next -- non-browser guard; jsdom always defines document. */
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      /* c8 ignore next -- non-browser guard; jsdom always defines document. */
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkHealth]);

  if (!down) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '8px 12px',
        backgroundColor: '#b45309',
        color: '#ffffff',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.01em',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      }}
    >
      <span aria-hidden="true">⚠️</span>
      Maintenance in progress — reconnecting…
    </div>
  );
};

export default MaintenanceBanner;
